import { CityContext } from "@/contexts/city-context-provider";
import { useContext, useState } from "react";
import {
  useCreateParkingRate,
  useSetParkingRateState,
  useGetParkingRates,
  usePublishParkingRate,
  useRemoveParkingRate,
  useUpdateParkingRate,
} from "./use-parking-rate-query";
import { ParkingRateStatusEnum, ParkingRateType } from "../payment-zone-area.type";
import { compilePricingPoints } from "../pricing-configurator.const";
import { toastError, toastSuccess } from "../parking-payment.const";

export const useParkingRate = () => {
  const { currentCity } = useContext(CityContext);
  const [isParkingRateModalOpen, setIsParkingRateModalOpen] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [selectedParkingRate, setSelectedParkingRate] = useState<ParkingRateType | null>(null);
  const [rateToSimulate, setRateToSimulate] = useState<ParkingRateType | null>(null);

  const { createParkingRate } = useCreateParkingRate();
  const { updateParkingRate } = useUpdateParkingRate();
  const { publishParkingRate } = usePublishParkingRate();
  const { removeParkingRate } = useRemoveParkingRate();
  const { setParkingRateState } = useSetParkingRateState();
  const { parkingRates, loading: parkingRatesLoading } = useGetParkingRates(currentCity?.id ?? "");

  const saveParkingRate = async (data: ParkingRateType) => {
    const parkingRateData: ParkingRateType = {
      ...data,
      cityId: currentCity?.id ?? "",
      compiledPricingPoints: compilePricingPoints(data),
    };
    if (data.id) {
      await updateParkingRate(parkingRateData);
    } else {
      await createParkingRate(parkingRateData);
    }
    setSelectedParkingRate(null);
  };

  const handlePublishParkingRate = async (rate: ParkingRateType) => {
    if (!rate.id || !rate.pricingConfiguration) return;
    try {
      await publishParkingRate({ id: rate.id, compiledPricingPoints: compilePricingPoints(rate) });
      toastSuccess("La tarification a été publiée avec succès.");
    } catch (error: unknown) {
      let message = "Une erreur est survenue lors de la publication.";
      if (error && typeof error === "object" && "message" in error) {
        const err = error as { message: string | string[] };
        message = Array.isArray(err.message) ? err.message.map((m) => `- ${m}`).join("\n") : err.message;
      }
      toastError(message);
    }
  };

  const handleSetParkingRateState = async (rate: ParkingRateType, status: ParkingRateStatusEnum) => {
    if (!rate.id) return;
    try {
      await setParkingRateState({ id: rate.id, status });
      const message =
        status === ParkingRateStatusEnum.ENABLED
          ? "La tarification a été réactivée."
          : "La tarification a été désactivée.";
      toastSuccess(message);
    } catch {
      toastError("Une erreur est survenue.");
    }
  };

  const handleEditParkingRate = (rate: ParkingRateType) => {
    setIsReadOnly(false);
    setSelectedParkingRate(rate);
    setIsParkingRateModalOpen(true);
  };

  const handleDuplicateParkingRate = (rate: ParkingRateType) => {
    setIsReadOnly(false);
    setSelectedParkingRate({
      ...rate,
      id: undefined,
      logicalRateId: undefined,
      status: undefined,
      version: undefined,
      restoredFromVersion: undefined,
      publishedAt: undefined,
      publishedBy: undefined,
      compiledPricingPoints: undefined,
    });
    setIsParkingRateModalOpen(true);
  };

  const handleViewParkingRate = (rate: ParkingRateType) => {
    setIsReadOnly(true);
    setSelectedParkingRate(rate);
    setIsParkingRateModalOpen(true);
  };

  const handleDeleteParkingRate = async (rate: ParkingRateType) => {
    if (!rate.id) return;
    await removeParkingRate(rate.id);
  };

  const handleCloseModal = () => {
    setIsParkingRateModalOpen(false);
    setSelectedParkingRate(null);
    setIsReadOnly(false);
  };

  const handleSimulateParkingRate = (rate: ParkingRateType) => {
    setRateToSimulate(rate);
  };

  const handleCloseSimulation = () => {
    setRateToSimulate(null);
  };

  return {
    isParkingRateModalOpen,
    setIsParkingRateModalOpen,
    isReadOnly,
    selectedParkingRate,
    saveParkingRate,
    handlePublishParkingRate,
    handleSetParkingRateState,
    handleEditParkingRate,
    handleDuplicateParkingRate,
    handleViewParkingRate,
    handleDeleteParkingRate,
    handleCloseModal,
    rateToSimulate,
    handleSimulateParkingRate,
    handleCloseSimulation,
    parkingRates,
    parkingRatesLoading,
  };
};
